// Paso 2 del onboarding externo de Tiendanube: conectar la tienda, traer los productos
// e importarlos a Atlas. Reusa la capa que ya existe (`abrirVentanaOAuth` + `ensureTnProducts`
// + `fireTnImport`): el usuario ya tiene empresa (la creó /tiendanube al registrarse), así que
// `tn-connect` funciona tal cual. `brandId=null` → la marca default para leer; para importar
// resolvemos el brand_id default real (n8n lo usa para asignar los productos a la marca).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  abrirVentanaOAuth,
  ensureTnProducts,
  fireTnImport,
  getTnStatus,
  type TnProduct,
} from '../../lib/tiendaNube';
import { createCompanyForUser } from '../../lib/createCompany';
import { MARCA_POR_DEFECTO } from '../../lib/altaDesdeCuestionario';
import { paisDelBrowser } from '../../lib/paisDelBrowser';
import { Feedback } from './authPrimitives';
import { traerPorLaImportacionMasiva } from '../../lib/traerCatalogoAlConectar';
import { necesitaPrimeraImagen, yaGeneroImagenes } from './primeraImagen';

// Import en tandas, igual criterio que AgregarVariosModal (el back procesa por worker/queue).
const TN_BATCH = 25;

/** Marca default de la empresa: es la que recibe los productos importados. */
async function marcaDefaultDe(companyId: string | null): Promise<string | null> {
  if (!companyId) return null;
  const { data } = await supabase
    .from('brand_profiles')
    .select('id')
    .eq('id_company', companyId)
    .eq('is_default', true)
    .maybeSingle();
  return (data as { id?: string } | null)?.id ?? null;
}

/**
 * Manda a importar los productos en tandas. El backend ENCOLA cada tanda y responde al
 * instante, así que esto no espera a que los productos existan en Atlas: van apareciendo
 * a medida que el worker los crea.
 *
 * Vive afuera del componente porque lo usan los DOS caminos que importan: el botón
 * "Empezar a generar" y el import silencioso de quien ya generó antes.
 */
async function importarEnTandas(
  productos: TnProduct[],
  destino: { userId: string | undefined; companyId: string | null; brandId: string | null },
): Promise<void> {
  // Primero la importación masiva (ADR-2026-10-05-integraciones-cierre, fase 2): trae TODO el catálogo,
  // sin tope, con aviso de repetidos. Si el backend no la toma, el import de siempre.
  // El import de siempre: sólo si la masiva la rechaza enseguida (review Greptile #160).
  const deSiempre = async () => {
    if (productos.length === 0) return;
    // Mismo mapeo TnProduct → payload que AgregarVariosModal.
    const payload = productos.map((t) => ({
      nombre: t.nombre,
      imgs: t.imgs,
      tn_product_id: t.id,
      variantes: t.variantes,
    }));
    for (let i = 0; i < payload.length; i += TN_BATCH) {
      await fireTnImport({ ...destino, productos: payload.slice(i, i + TN_BATCH) });
    }
  };
  if (await traerPorLaImportacionMasiva(destino.brandId)) return;
  await deSiempre();
}

export default function TiendanubeConectar() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [products, setProducts] = useState<TnProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Install-first: la marca donde tn-link-claim vinculó la tienda (para traer/importar los productos
  // ahí, no en la default).
  const [claimBrandId, setClaimBrandId] = useState<string | null>(null);
  /**
   * `true` = esta cuenta YA generó imágenes, así que la pantalla de la primera imagen no
   * le sirve. Se resuelve una sola vez en el efecto de abajo y lo leen los dos caminos.
   * Ref y no estado: no cambia nada de lo que se dibuja, sólo a dónde se sale.
   */
  const vuelveAlEstudioRef = useRef(false);

  // Sin sesión → volver a la pantalla de registro/login (no se llega acá sin autenticar).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!data.user) {
        navigate('/tiendanube', { replace: true });
        return;
      }
      // Salvavidas: quien llegó por Google/login SIN empresa la necesita antes de importar (el
      // registro por mail ya la crea, y "Continuar" también). Rubro placeholder 'Otros' — la IA lo
      // ajusta al conectar la tienda. `createCompanyForUser` es idempotente.
      const { data: prof } = await supabase
        .from('profiles')
        .select('id_company, name')
        .eq('id', data.user.id)
        .maybeSingle();
      if (cancelled) return;
      let idCompany = (prof as { id_company?: string | null } | null)?.id_company ?? null;
      if (!idCompany) {
        const meta = data.user.user_metadata as { full_name?: string } | undefined;
        try {
          await createCompanyForUser({
            userId: data.user.id,
            name:
              (prof as { name?: string } | null)?.name ||
              meta?.full_name ||
              data.user.email?.split('@')[0] ||
              'Mi empresa',
            pais: paisDelBrowser() ?? 'Otro',
            companyName: MARCA_POR_DEFECTO,
            industry: 'Otros',
          });
        } catch {
          /* si falla la creación, no trabamos la pantalla: el import avisará si falta empresa */
        }
        if (cancelled) return;
        // Releemos: la empresa recién creada es la que va a recibir los productos, y sin
        // su id no se puede saber si esta cuenta ya generó algo.
        const { data: profNuevo } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', data.user.id)
          .maybeSingle();
        if (cancelled) return;
        idCompany = (profNuevo as { id_company?: string | null } | null)?.id_company ?? null;
      }

      // La pregunta que decide si la pantalla de "generá tu primera imagen" le sirve a
      // esta cuenta — ADR-2026-09-17-el-onboarding-de-tiendanube-es-para-quien-no-genero-nada.
      // Se resuelve ACÁ, una sola vez y para cualquier puerta de entrada (claim, Google,
      // conectar a mano), así los dos caminos de salida no pueden discrepar.
      const yaGenero = await yaGeneroImagenes(idCompany);
      if (cancelled) return;
      vuelveAlEstudioRef.current = !necesitaPrimeraImagen(yaGenero);

      // Install-first: si venimos con un claim (tienda ya autorizada, en espera), la vinculamos a ESTA
      // cuenta con tn-link-claim (sin re-hacer OAuth) y traemos sus productos. Ver edge tn-link-claim.
      let claimPend: string | null = null;
      try {
        claimPend = localStorage.getItem('tn-install-claim');
      } catch {
        /* storage no disponible */
      }
      if (claimPend) {
        try {
          const { data: linkData, error: linkErr } = await supabase.functions.invoke('tn-link-claim', {
            body: { claim: claimPend },
          });
          if (cancelled) return;
          if (linkErr) {
            let code: string | undefined;
            let serverMsg: string | undefined;
            try {
              const b = await (linkErr as { context?: Response }).context?.json();
              code = b?.code;
              serverMsg = b?.error;
            } catch {
              /* sin cuerpo legible */
            }
            // El claim se descarta SÓLO cuando reintentar no cambia nada: éxito (más abajo) o error
            // TERMINAL. En errores transitorios (500 → tn-link-claim libera `claimed_at`) NO lo borramos,
            // así una recarga reintenta sin obligar al comerciante a re-autorizar la tienda de cero.
            // `sin_empresa` NO es terminal: tn-link-claim lo devuelve ANTES de consumir el claim (si el
            // salvavidas que crea la empresa falló transitorio), así que una recarga puede reintentar.
            const terminal =
              code === 'tienda_en_otra_cuenta' ||
              code === 'claim_invalido' ||
              code === 'claim_vencido';
            if (terminal) {
              try {
                localStorage.removeItem('tn-install-claim');
              } catch {
                /* noop */
              }
              setError(serverMsg || 'No se pudo conectar tu tienda. Probá de nuevo.');
              setChecking(false);
              return;
            }
            // Transitorio: dejamos el claim y avisamos para que una recarga reintente.
            setError(serverMsg || 'No pudimos conectar tu tienda. Recargá la página para reintentar.');
            setChecking(false);
            return;
          }
          // Éxito: la tienda quedó vinculada → el claim ya cumplió, lo borramos.
          try {
            localStorage.removeItem('tn-install-claim');
          } catch {
            /* noop */
          }
          const bId = (linkData as { brand_id?: string | null } | null)?.brand_id ?? null;
          setClaimBrandId(bId);
          try {
            // tn-products puede devolver 0 por timing justo tras vincular la tienda (la consulta a
            // Tiendanube pega demasiado rápido). Reintentamos un par de veces con pausa antes de dar
            // el catálogo por vacío, para no meter al usuario nuevo sin sus productos.
            let res = await ensureTnProducts(bId);
            for (let intento = 0; res.products.length === 0 && intento < 2; intento++) {
              await new Promise((r) => setTimeout(r, 1500));
              if (cancelled) return;
              res = await ensureTnProducts(bId);
            }
            if (cancelled) return;
            // Quien YA generó imágenes no vuelve a pasar por el onboarding: la tienda
            // quedó vinculada arriba y entra derecho a la app. Antes de entrar se importa
            // en silencio lo que falte —`res.products` ya viene SIN los productos que
            // están en Atlas, porque `tn-products` los descuenta—, así saltear la pantalla
            // no le cuesta el catálogo a nadie.
            if (!necesitaPrimeraImagen(yaGenero)) {
              // La masiva se pide aunque la lectura venga vacía (puede dar 0 justo tras vincular; review #160).
              {
                try {
                  await importarEnTandas(res.products, {
                    userId: data.user.id,
                    companyId: idCompany,
                    brandId: bId,
                  });
                  if (cancelled) return;
                  // Mismos segundos que el botón "Empezar": que el worker arranque antes
                  // de entrar, para que los productos ya estén apareciendo.
                  await new Promise((r) => setTimeout(r, 3500));
                } catch {
                  /* best-effort: el catálogo se puede traer después desde la app */
                }
              }
              if (cancelled) return;
              // Sin `setChecking(false)`: el loader se queda hasta que navega, para no
              // mostrar un parpadeo de la pantalla de conectar que no corresponde.
              navigate('/', { replace: true });
              return;
            }
            setStoreName(res.storeName);
            setProducts(res.products);
            setConnected(true);
          } catch (e) {
            if (cancelled) return;
            setError(e instanceof Error ? e.message : 'No se pudieron traer los productos.');
          }
          setChecking(false);
          return;
        } catch {
          if (cancelled) return;
          // Excepción de red (transitoria): NO borramos el claim; que una recarga reintente.
          setError('No pudimos conectar tu tienda. Recargá la página para reintentar.');
          setChecking(false);
          return;
        }
      }

      // Si el usuario YA tiene su tienda conectada, no se la pedimos de nuevo. A dónde va
      // lo decide la MISMA regla que los otros dos caminos: quien todavía no generó nada
      // pasa por la pantalla de la primera imagen, aunque su tienda ya esté conectada.
      // Antes esta salida mandaba siempre a la app, y era la única que no consultaba la
      // regla — con la tienda conectada pero sin haber generado, el onboarding se salteaba.
      try {
        const status = await getTnStatus(null);
        if (cancelled) return;
        if (status.connected) {
          // Al selector de productos no se entra con las manos vacías: acá nadie importó
          // nada todavía (no hubo claim ni botón "Empezar"), así que si la tienda tiene
          // productos que no están en Atlas, se traen antes. Sin esto la pantalla saldría
          // vacía diciendo "estamos trayendo tus productos" sin que nadie los esté
          // trayendo. `ensureTnProducts` no abre OAuth acá: la tienda ya está conectada.
          if (!vuelveAlEstudioRef.current) {
            try {
              const res = await ensureTnProducts(null);
              if (cancelled) return;
              {
                await importarEnTandas(res.products, {
                  userId: data.user.id,
                  companyId: idCompany,
                  brandId: await marcaDefaultDe(idCompany),
                });
                if (cancelled) return;
                await new Promise((r) => setTimeout(r, 3500));
              }
            } catch {
              /* best-effort: el selector deja subir una foto igual */
            }
            if (cancelled) return;
          }
          navigate(vuelveAlEstudioRef.current ? '/' : '/tiendanube/producto', {
            replace: true,
          });
          return;
        }
      } catch {
        /* si el chequeo falla, mostramos el botón de conectar igual */
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleConnect = async () => {
    setError(null);
    // El popup se abre EN el click, sincrónico, antes de cualquier await (Safari/iOS).
    // Ver ADR-2026-08-07-tiendanube-popup-safari.
    const popup = abrirVentanaOAuth();
    setConnecting(true);
    try {
      const { products: prods, storeName: name } = await ensureTnProducts(null, { popup });
      setStoreName(name);
      setProducts(prods);
      setConnected(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg !== 'CANCELLED') setError(msg || 'No se pudo conectar tu tienda. Probá de nuevo.');
    } finally {
      setConnecting(false);
    }
  };

  const handleEmpezar = async () => {
    setError(null);
    setImporting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      // Empresa + marca default del usuario (para asignar los productos importados).
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user?.id ?? '')
        .maybeSingle();
      const companyId = (profile as { id_company?: string | null } | null)?.id_company ?? null;
      // Install-first: importamos a la marca donde quedó la tienda (claimBrandId); si no, a la default.
      const brandId = claimBrandId ?? (await marcaDefaultDe(companyId));

      await importarEnTandas(products, { userId: user?.id, companyId, brandId });
      // Damos unos segundos a que n8n arranque a crear los productos (mismo criterio que el modal),
      // y entramos a la app: los productos van apareciendo a medida que el import los procesa.
      await new Promise((r) => setTimeout(r, 3500));
      // Misma regla que el camino del claim: la pantalla de la primera imagen es para
      // quien todavía no generó nada. Acá casi siempre es alguien nuevo, pero el camino
      // existe —quien desconectó su tienda desde Atlas y la reconecta desde acá— y las
      // dos salidas tienen que decidir igual.
      navigate(vuelveAlEstudioRef.current ? '/' : '/tiendanube/producto', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron traer los productos. Probá de nuevo.');
      setImporting(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center bg-white">
        <Loader2 className="size-6 animate-spin text-[#71717B]" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-white">
      <div className="mx-auto flex w-full max-w-[440px] animate-fade-in flex-col items-center gap-6 px-5 pb-10 pt-16 text-center">
        <img
          src="/images/estudio-atlas-logo-solo.svg"
          alt="Estudio Atlas"
          draggable={false}
          className="h-12 w-auto select-none"
        />

        {!connected ? (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-[24px] font-[600] leading-[30px] text-[#18181B]">
                Conectá tu Tiendanube
              </h1>
              <p className="text-[14px] font-[500] leading-[20px] text-[#52525C]">
                Traemos tus productos para que generes imágenes profesionales con ellos.
              </p>
            </div>

            <Feedback error={error} message={null} />

            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {connecting && <Loader2 className="size-4 animate-spin" />}
              {connecting ? 'Conectando tu tienda…' : 'Conectar mi Tiendanube'}
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-[24px] font-[600] leading-[30px] text-[#18181B]">
                ¡Tienda conectada! 🎉
              </h1>
              <p className="text-[14px] font-[500] leading-[20px] text-[#52525C]">
                Conectamos{' '}
                <span className="font-[700] text-[#18181B]">{storeName || 'tu tienda'}</span> y
                encontramos <span className="font-[700] text-[#18181B]">{products.length}</span>{' '}
                {products.length === 1 ? 'producto' : 'productos'}.
              </p>
            </div>

            <Feedback error={error} message={null} />

            <button
              type="button"
              onClick={handleEmpezar}
              disabled={importing}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importing && <Loader2 className="size-4 animate-spin" />}
              {importing ? 'Trayendo tus productos…' : 'Empezar a generar'}
            </button>
            {importing && (
              <p className="text-[13px] font-[500] leading-[18px] text-[#71717B]">
                Estamos trayendo tus productos a Atlas. Van a aparecer en un ratito.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
